import axios from 'axios';
import { baseUrl } from '../utils/config';

// Old PaymentService logic (deprecated, kept for reference):
// export interface CreateCheckoutSessionPayload {
//   product_name: string; // cart_id or similar identifier
//   amount_cents: number; // total amount in cents
//   currency: string; // e.g., 'TND'
//   return_url: string; // full URL to return to after payment
// }
// export interface CheckoutSessionResponse {
//   path?: string; // URL to redirect the user for payment
//   checkout_url?: string;
//   url?: string;
// }

// /**
//  * PaymentService handles payment session creation for orders.
//  * It returns a redirect path for the user to complete payment.
//  */
// export const PaymentService = {
//   /**
//    * Create a checkout session for payment.
//    * @param payload - The payment session details
//    * @returns The redirect path for payment
//    */
//   async createCheckoutSession(payload: CreateCheckoutSessionPayload): Promise<CheckoutSessionResponse> {
//     try {
//       const response = await axios.post(`${baseUrl}/stripe/create-checkout-session`, payload);
//       // Expecting response.data.path to be the redirect URL
//       return response.data;
//     } catch (error) {
//       console.error('Failed to create checkout session:', error);
//       throw error;
//     }
//   },
// };

// New PaymentService implementation for /checkout/
export interface CartItem {
  product_id: string;
  name: string;
  quantity: number;
  price: number;
}

export interface CustomerInfo {
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  postalCode: string;
  country: string;
  paymentMethod: string;
}

export interface CreateCheckoutSessionPayload {
  user_id: string;
  cart_items: CartItem[];
  customer_info: CustomerInfo;
  currency: string;
  return_url: string;
  delivery_fee?: number; // Optional delivery fee, will be calculated on backend if not provided
}

export interface CheckoutSessionResponse {
  checkout_url: string;
}

export interface OrderBySessionResponse {
  order_id: string;
  payment_status: string;
  status: string;
  total: number;
  delivery_fee: number;
  customer_info: CustomerInfo;
  currency: string;
  products?: Array<{
    productId: string;
    productName: string;
    quantity: number;
    price: number;
  }>;
}

// New interface for direct order creation (payment on delivery)
export interface CreateOrderPayload {
  user_id: string;
  cart_items: CartItem[];
  customer_info: CustomerInfo;
  currency: string;
  payment_method: string; // "online" or "delivery"
  delivery_fee?: number; // Optional delivery fee, will be calculated on backend if not provided
}

export interface CreateOrderResponse {
  success: boolean;
  order_id: string;
  delivery_fee: number;
  total_amount: number;
  message: string;
}

const api = axios.create({ baseURL: baseUrl });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers = config.headers || {};
    (config.headers as any).Authorization = `Bearer ${token}`;
  }
  return config;
});

const PaymentService = {
  // Existing method for Stripe checkout (ONLINE PAYMENT)
  async createCheckoutSession(payload: CreateCheckoutSessionPayload): Promise<CheckoutSessionResponse> {
    try {
      const response = await api.post(`/checkout/`, payload);
      return response.data;
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      throw error;
    }
  },

  // Create a Stripe checkout session for a consultation
  async createConsultationCheckoutSession(params: { consultation_id: number; return_url: string }): Promise<CheckoutSessionResponse> {
    try {
      const response = await api.post(`/consultations/create-checkout-session`, params);
      return response.data;
    } catch (error) {
      console.error('Failed to create consultation checkout session:', error);
      throw error;
    }
  },

  async createConsultationCheckoutSessionInit(params: {
    professionnel_id: number;
    date: string;
    heure: string;
    nom_patient: string;
    email: string;
    pays: string;
    telephone: string;
    sujet: string;
    return_url: string;
  }): Promise<CheckoutSessionResponse> {
    try {
      const response = await api.post(`/consultations/create-checkout-session`, params);
      return response.data;
    } catch (error) {
      console.error('Failed to create consultation checkout session (init):', error);
      throw error;
    }
  },

  // New method for direct order creation (PAYMENT ON DELIVERY)
  async createOrderDirectly(payload: CreateOrderPayload): Promise<CreateOrderResponse> {
    try {
      const response = await api.post(`/orders/create-new-order`, payload);
      return response.data;
    } catch (error) {
      console.error('Failed to create order directly:', error);
      throw error;
    }
  },

  async getOrderBySession(session_id: string): Promise<OrderBySessionResponse> {
    try {
      const response = await api.get(`/orders/by-session/${session_id}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get order by session:', error);
      throw error;
    }
  },

  async getConsultationBySession(session_id: string): Promise<{
    payment_status: string;
    consultation_id?: number;
    video_status?: string;
  }> {
    try {
      const response = await api.get(`/consultations/by-session/${session_id}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get consultation by session:', error);
      throw error;
    }
  }
};

export default PaymentService; 